// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import React, { useMemo, useRef, useState } from 'react';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import { Box, ClickAwayListener, Fab, Fade, MenuItem, MenuList, Paper, Popper, Typography, Tooltip } from '@mui/material';
import { useTheme } from '@mui/system';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import CreateFolderDialog from 'Components/CreateFolderDialog';
import { useAppDispatch } from 'Hooks';
import { useNavigate } from 'react-router';
import { GetFolderDetailsRoute } from 'Utils/routes';
import { isPrimaryPress } from 'Utils/pointerPress';
import { DRAG_ACTIVATION_DISTANCE } from 'Features/projectList/dragSensors';
import DeleteFolderDialog from 'Components/DeleteFolderDialog';
import stylesDef from './styles';
import { deleteFolderThunk } from '../folderListThunk';
import { SetSelectedFolderAction } from '../folderListActions';

// No defaults for the optional props: defaultProps is deprecated for function components, and
// absence is the meaning (a card that opens its folder rather than scoping the map; not selected).
/* eslint-disable react/require-default-props */
interface IFolderListItemProps {
  folder: IFolder;
  showMapview: boolean;
  // Whether the card can be dragged to reorder folders; the page turns it off while searching
  reorderable: boolean;
  // Given by the map rail, where a folder card scopes the map instead of leaving for the
  // folder's page. Absent elsewhere, where clicking opens the folder as it always has.
  onSelect?: (folder: IFolder) => void;
  isSelected?: boolean;
}
/* eslint-enable react/require-default-props */

const FolderListItem = ({ folder, showMapview, reorderable, onSelect, isSelected }: IFolderListItemProps) => {
  // Constants
  const theme = useTheme();
  const styles = stylesDef(theme, showMapview);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  // State
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [showRenameFolderModal, setShowRenameFolderModal] = useState(false);
  const [showDeleteFolderModal, setShowDeleteFolderModal] = useState(false);
  const [loading, setLoading] = useState(false);

  // useMemo
  const isMenuOpen = useMemo(() => Boolean(anchorEl), [anchorEl]);

  // Methods
  const handleMenuClose = () => setAnchorEl(null);

  const handleMenuOpen = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => setAnchorEl(event.currentTarget);

  const renameFolderClick = () => {
    setShowRenameFolderModal(true);
  };

  const deleteFolderClick = async () => {
    if (folder?.projectCount > 0) {
      setShowDeleteFolderModal(true);
    } else {
      deleteFolder(folder?._id);
    }
  };

  const deleteFolder = async (folderId: string) => {
    setLoading(true);
    // Call the delete folder thunk or API here
    const result = await dispatch(deleteFolderThunk(folderId));

    // If the deletion is successful, fetch all folders again
    if (result.success) {
      // Close the delete confirmation modal
      setShowDeleteFolderModal(false);
      setLoading(false);
    }
  };

  const handleFolderClick = () => {
    if (onSelect) {
      onSelect(folder);
      return;
    }

    if (!folder?._id) return;

    dispatch(SetSelectedFolderAction(folder));

    const url = GetFolderDetailsRoute(folder._id);
    navigate(url);
  };

  // Drag to reorder: the sortable hook makes the card draggable when the page allows it, keeps it a
  // drop target for projects, and slides it over while another folder is dragged past (the live
  // preview of the resulting order). While this card is the one being dragged, its in-flow copy is
  // the placeholder slot and the moving card is drawn by the page's drag overlay.
  const {
    attributes, listeners, setNodeRef, transform, transition, isDragging, isOver, active,
  } = useSortable({
    id: folder._id,
    data: { type: 'folder' },
    disabled: !reorderable,
  });
  const isProjectOver = isOver && active?.data.current?.type === 'project';

  // Click vs drag, as on the project card: a main-button pointer that moves less than the drag
  // sensor's activation distance between down and up is a click and opens the folder; anything
  // further belongs to the drag. Other buttons are the browser's (new tab, context menu).
  const dragStart = useRef<{ x: number, y: number } | null>(null);

  const handlePointerDown = (e: React.PointerEvent) => {
    if (!isPrimaryPress(e)) return;
    dragStart.current = { x: e.clientX, y: e.clientY };
    listeners?.onPointerDown?.(e);
  };

  const handlePointerUp = (e: React.PointerEvent) => {
    if (!isPrimaryPress(e)) return;
    const menuButton = document.getElementById(`folder-menu-${folder._id}`);
    if (menuButton && menuButton.contains(e.target as Node)) return;
    if (!dragStart.current) return;
    const dx = Math.abs(e.clientX - dragStart.current.x);
    const dy = Math.abs(e.clientY - dragStart.current.y);
    dragStart.current = null;
    if (dx < DRAG_ACTIVATION_DISTANCE && dy < DRAG_ACTIVATION_DISTANCE && !isMenuOpen) {
      handleFolderClick();
    }
  };

  return (
    <>
      <Box
        ref={setNodeRef}
        role={attributes.role}
        tabIndex={attributes.tabIndex}
        aria-disabled={attributes['aria-disabled']}
        aria-pressed={attributes['aria-pressed']}
        aria-roledescription={attributes['aria-roledescription']}
        aria-describedby={attributes['aria-describedby']}
        sx={{
          ...styles.mainContiner,
          ...((isProjectOver || isSelected) && styles.activeDroppableHover),
          ...(isDragging && styles.dropSlot),
          transform: CSS.Transform.toString(transform),
          transition,
        }}
        onPointerDown={handlePointerDown}
        onPointerUp={handlePointerUp}
      >
        <Box sx={styles.folderItemContainer}>
          <Box sx={styles.folderIconContainer}>
            <FolderOutlinedIcon />
          </Box>
          <Box sx={styles.folderDetails}>
            {folder?.name?.length > 26 ? (
              <Tooltip title={folder.name} placement="top" arrow>
                <Typography variant="h5" sx={styles.folderNameText}>
                  {folder.name ?? ''}
                </Typography>
              </Tooltip>
            ) : (
              <Typography variant="h5" sx={styles.folderNameText}>
                {folder.name ?? ''}
              </Typography>
            )}
            <Typography variant="body2" sx={styles.projectCount}>{`${folder?.projectCount ?? 0} ${folder?.projectCount > 1 ? 'Projects' : 'Project'}`}</Typography>
          </Box>
        </Box>
        <Fab
          id={`folder-menu-${folder._id}`}
          sx={styles.moreIconContainer}
          className={isMenuOpen ? 'showFab' : ''}
          onClick={(e) => {
            e.stopPropagation();
            handleMenuOpen(e);
          }}
        >
          <MoreHorizIcon />
        </Fab>
        <Popper sx={styles.folderListItemPopper} open={isMenuOpen} anchorEl={anchorEl} transition>
          {({ TransitionProps }) => (
            <ClickAwayListener onClickAway={handleMenuClose}>
              <Fade {...TransitionProps} timeout={350}>
                <Paper>
                  <MenuList sx={styles.menuList}>
                    <MenuItem
                      sx={styles.fontWeight}
                      onClick={async (e) => {
                        e.stopPropagation();
                        // Add Pendo Track Events
                        window?.pendo?.track('Rename Folder');
                        handleMenuClose();
                        await renameFolderClick();
                      }}
                    >
                      Rename
                    </MenuItem>
                    <MenuItem
                      sx={styles.fontWeight}
                      onClick={async (e) => {
                        e.stopPropagation();
                        // Add Pendo Track Events
                        window?.pendo?.track('Delete Folder');
                        handleMenuClose();
                        await deleteFolderClick();
                      }}
                    >
                      Delete
                    </MenuItem>
                  </MenuList>
                </Paper>
              </Fade>
            </ClickAwayListener>
          )}
        </Popper>
      </Box>

      {/* Create/Rename Folder Dialog */}
      <CreateFolderDialog
        showDialog={showRenameFolderModal}
        title="Rename Folder"
        isEdit
        folderName={folder?.name}
        folderId={folder?._id}
        setShowDialog={setShowRenameFolderModal}
      />

      {/* Delete Folder Dialog */}
      <DeleteFolderDialog
        showDialog={showDeleteFolderModal}
        setShowDialog={setShowDeleteFolderModal}
        onDelete={() => deleteFolder(folder?._id)}
        loading={loading}
      />
    </>
  );
};

export default FolderListItem;
